import { Component, Input } from '@angular/core';
import { BaseparameterService, ParameterConfigResponse } from '../../../core/base-api';
import { map } from 'rxjs/operators';
import { LocalLanguageService } from '../../../core/services/local-language.service';
import { Observable } from 'rxjs';
import { GrundstueckDetailEntryMeta } from '../../../core/gb-api';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AbstractComponent } from '../abstract-component/abstract.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AsyncPipe } from '@angular/common';
import { FremdsystemVisibilityFilterPipe } from '../../pipes/simple/fremdsystem-visibility-filter-pipe';
import GrundstueckArtEnum = GrundstueckDetailEntryMeta.GrundstueckArtEnum;
import GrundstueckArtParamEnum = ParameterConfigResponse.GrundstueckArtEnumEnum;

@Component({
  selector: 'cap-fremdsysteme',
  templateUrl: './fremdsysteme.component.html',
  styleUrls: ['./fremdsysteme.component.scss'],
  imports: [NgbTooltip, AsyncPipe, FremdsystemVisibilityFilterPipe]
})
export class FremdsystemeComponent extends AbstractComponent {
  private static readonly ICP_FREMDSYSTEM_URL = 'ICP_FREMDSYSTEM_URL_';
  private static readonly ICP_FREMDSYSTEM_NAME_DE = 'ICP_FREMDSYSTEM_NAME_DE_';
  private static readonly ICP_FREMDSYSTEM_NAME_FR = 'ICP_FREMDSYSTEM_NAME_FR_';
  private static readonly ICP_FREMDSYSTEM_ICON = 'ICP_FREMDSYSTEM_ICON_';
  private static readonly ICP_FREMDSYSTEM_GRUNDSTUECK_ART = 'ICP_FREMDSYSTEM_GRUNDSTUECK_ART_';

  @Input()
  gsEntry: GrundstueckDetailEntryMeta;
  @Input()
  isListEntry: boolean = false;

  fremdsysteme: Observable<Fremdsystem[]>;

  constructor(
    private baseparameterService: BaseparameterService,
    private localLanguageService: LocalLanguageService,
    private translateService: TranslateService,
    private sanitizer: DomSanitizer
  ) {
    super();
    this.fremdsysteme = this.loadFremdsysteme();
    this.addSub(
      this.translateService.onLangChange.subscribe(() => {
        this.fremdsysteme = this.loadFremdsysteme();
      })
    );
  }

  private loadFremdsysteme() {
    return this.baseparameterService.getAllParameter().pipe(
      map((parameterConfigResponse) => {
        const urlWithEgridParameterReplaced = (p: ParameterConfigResponse, index: number): SafeUrl => {
          const url = p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_URL + index] as unknown as string;
          return this.sanitizer.bypassSecurityTrustUrl(url.replace('{egrid}', this.gsEntry.egrid.value));
        };
        return this.mapFremdsysteme(
          this.localLanguageService,
          this.nameByLanguage,
          urlWithEgridParameterReplaced,
          parameterConfigResponse,
          this.gsEntry
        );
      })
    );
  }

  private mapFremdsysteme(
    languageService: LocalLanguageService,
    nameByLanguage: NameByLanguage,
    urlWithEgridParameterReplaced: UrlWithEgridParameterReplaced, //
    p: ParameterConfigResponse,
    gsEntry: GrundstueckDetailEntryMeta
  ): Fremdsystem[] {
    const fremdsysteme: Fremdsystem[] = [];
    for (let i = 1; i <= 3; i++) {
      if (
        p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_NAME_DE + i] &&
        p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_NAME_FR + i]
      ) {
        fremdsysteme.push(
          new Fremdsystem(
            urlWithEgridParameterReplaced(p, i),
            nameByLanguage(languageService, p, i),
            p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_ICON + i], //
            p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_GRUNDSTUECK_ART + i] as GrundstueckArtParameter,
            gsEntry
          )
        );
      }
    }
    return fremdsysteme;
  }

  private nameByLanguage(languageService: LocalLanguageService, p: ParameterConfigResponse, index: number): object {
    return languageService.getLocalLanguage() === 'fr'
      ? p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_NAME_FR + index]
      : p.parameters[FremdsystemeComponent.ICP_FREMDSYSTEM_NAME_DE + index];
  }
}

export class Fremdsystem {
  readonly isVisible: boolean;

  constructor(
    readonly url: SafeUrl,
    readonly label: object,
    readonly icon: object,
    fremdsystemVisibilityByGrundstueckArt: GrundstueckArtParameter,
    gsEntry: GrundstueckDetailEntryMeta
  ) {
    this.isVisible = this.calculateVisibilityByGrundstueckArt(gsEntry, fremdsystemVisibilityByGrundstueckArt);
  }

  calculateVisibilityByGrundstueckArt(gsEntry: GrundstueckDetailEntryMeta, visibilityByGrundstueckArt: GrundstueckArtParameter): boolean {
    switch (gsEntry.grundstueckArt) {
      case GrundstueckArtEnum.STOCKWERK:
        return visibilityByGrundstueckArt.attributes.STOCKWERK;
      case GrundstueckArtEnum.LIEGENSCHAFT:
        return visibilityByGrundstueckArt.attributes.LIEGENSCHAFT;
      case GrundstueckArtEnum.MITEIGENTUM:
        return visibilityByGrundstueckArt.attributes.MITEIGENTUM;
      case GrundstueckArtEnum.UNBEKANNT:
        return visibilityByGrundstueckArt.attributes.UNBEKANNT;
      case GrundstueckArtEnum.FOLIO:
        return visibilityByGrundstueckArt.attributes.FOLIO;
      case GrundstueckArtEnum.BERGWERK:
        return visibilityByGrundstueckArt.attributes.BERGWERK;
      case GrundstueckArtEnum.SDR:
        return gsEntry.flaechenMaessigNichtAusgeschieden
          ? visibilityByGrundstueckArt.attributes.SDR_FLAECHENMAESSIG_NICHT_AUSGESCHIEDEN
          : visibilityByGrundstueckArt.attributes.SDR;
    }
  }
}

type NameByLanguage = (languageService: LocalLanguageService, p: ParameterConfigResponse, index: number) => object;

type UrlWithEgridParameterReplaced = (p: ParameterConfigResponse, index: number) => SafeUrl;

type GrundstueckArtParameter = {
  attributes: {
    [key in GrundstueckArtParamEnum]?: boolean;
  };
};
